import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { api } from '../api/client.js';
import type { Freshness, QueryDescriptor, StoreStats } from './dataStore.js';
import { dataStore } from './dataStore.js';
import { Prefetcher, type PrefetchProgress } from './prefetch.js';

/**
 * The React binding for the client store.
 *
 * Mounted once, inside the authenticated shell, because it needs two things the shell has and
 * nothing above it does: the signed-in address (the store is namespaced by owner — see
 * `dataStore.ts`) and the guarantee that a session exists, so the prefetch is not sixty requests
 * that will all come back 401.
 *
 * Pages do not touch the store directly. They call `useQuery(queries.something(...))`, which:
 *
 * - paints from the store on the **first** render when an entry exists, so a prefetched page has
 *   no loading state at all;
 * - revalidates behind the page when the entry is past its six hours, without blanking it;
 * - only shows a loading state when there is genuinely nothing to show.
 */

export interface RefreshResult {
  ok: boolean;
  message: string;
}

export interface HardRefreshResult extends RefreshResult {
  snapshotId?: string;
  asOf?: string | null;
  purgedKeys?: number;
}

interface StoreApi {
  /** Discard nothing, mark everything stale, refill. The Settings *Refresh* button. */
  refresh: () => Promise<RefreshResult>;
  /**
   * Drop the server's cached responses for the active snapshot, then discard the local copy
   * outright and refill from a cold server cache. The Settings *Hard refresh* button.
   */
  hardRefresh: () => Promise<HardRefreshResult>;
  prefetcher: Prefetcher;
}

const StoreContext = createContext<StoreApi | null>(null);

export function StoreProvider({ owner, children }: { owner: string; children: ReactNode }) {
  // One prefetcher for the life of the provider. Recreating it on every render would restart the
  // run and lose the progress the Settings page is reading.
  const prefetcher = useMemo(() => new Prefetcher(dataStore), []);

  useEffect(() => {
    // Adopts this owner's persisted entries, or wipes someone else's.
    dataStore.open(owner);
    void prefetcher.start();

    return () => prefetcher.stop();
  }, [owner, prefetcher]);

  const refresh = useCallback(async (): Promise<RefreshResult> => {
    dataStore.invalidateAll();
    await prefetcher.start();
    const { failed, completed } = prefetcher.getProgress();

    return failed === 0
      ? { ok: true, message: `Refreshed ${completed} of the app's data sets from the server.` }
      : {
          ok: false,
          message: `Refreshed ${completed - failed} data sets; ${failed} could not be fetched. Open a page to see the specific error.`,
        };
  }, [prefetcher]);

  const hardRefresh = useCallback(async (): Promise<HardRefreshResult> => {
    let purge: Awaited<ReturnType<typeof api.purgeServerCache>>;
    try {
      purge = await api.purgeServerCache();
    } catch (err) {
      return {
        ok: false,
        message:
          err instanceof Error
            ? `The server cache could not be cleared. ${err.message}`
            : 'The server cache could not be cleared.',
      };
    }

    // `clear`, not `invalidateAll`: a hard refresh is a request for a cold start on both sides,
    // and keeping the old values on screen while the server recomputes would hide whether it did.
    dataStore.clear();
    await prefetcher.start();
    const { failed, completed } = prefetcher.getProgress();

    return {
      ok: failed === 0,
      snapshotId: purge.snapshotId,
      asOf: purge.asOf,
      purgedKeys: purge.purgedKeys,
      message:
        failed === 0
          ? `Cleared ${purge.purgedKeys} server cache entries and refetched ${completed} data sets.`
          : `Cleared ${purge.purgedKeys} server cache entries; ${failed} of ${completed} refetches failed.`,
    };
  }, [prefetcher]);

  const value = useMemo<StoreApi>(
    () => ({ refresh, hardRefresh, prefetcher }),
    [refresh, hardRefresh, prefetcher],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

/**
 * The refresh actions and the prefetcher.
 *
 * Throws outside the provider rather than returning a no-op: a Refresh button that silently did
 * nothing would be worse than a page that failed to render.
 */
export function useStoreActions(): StoreApi {
  const actions = useContext(StoreContext);
  if (!actions) throw new Error('useStoreActions must be used inside <StoreProvider>');
  return actions;
}

export interface QueryResult<T> {
  data: T | null;
  /** Nothing to paint yet. A prefetched entry never produces this. */
  loading: boolean;
  /** Data is on screen and a newer copy is on its way. */
  revalidating: boolean;
  error: Error | null;
  reload: () => void;
  freshness: Freshness;
  fetchedAt: number | null;
}

/**
 * Read one query through the store.
 *
 * The descriptor is rebuilt on every render by its caller (`queries.overview(period)`), so the
 * effect keys off the **key** and holds the descriptor in a ref — the same reason `useAsync` holds
 * its callback in one. Depending on the object would refetch on every render.
 */
export function useQuery<T>(descriptor: QueryDescriptor<T>): QueryResult<T> {
  const { key } = descriptor;

  const current = useRef(descriptor);
  current.current = descriptor;

  const [error, setError] = useState<Error | null>(null);

  const entry = useSyncExternalStore(dataStore.subscribe, () => dataStore.entry<T>(key));

  useEffect(() => {
    let cancelled = false;
    setError(null);

    if (dataStore.freshness(key, Date.now(), current.current.staleWhen) === 'fresh') return;

    dataStore.run(current.current).catch((err: unknown) => {
      if (cancelled) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    });

    return () => {
      cancelled = true;
    };
  }, [key]);

  const reload = useCallback(() => {
    setError(null);
    dataStore.run(current.current, { force: true }).catch((err: unknown) => {
      setError(err instanceof Error ? err : new Error(String(err)));
    });
  }, []);

  const data = (entry?.value as T | undefined) ?? null;

  return {
    data,
    loading: data === null && error === null,
    revalidating: data !== null && dataStore.isFetching(key),
    error,
    reload,
    freshness: dataStore.freshness(key, Date.now(), descriptor.staleWhen),
    fetchedAt: entry?.fetchedAt ?? null,
  };
}

/**
 * Read a stored entry WITHOUT ever fetching it (spec 008, R-14, FR-AL04).
 *
 * The header bell is on every page, and it must not cost a request per page: it shows the copy the
 * store already holds — prefetched at sign-in, kept fresh by the sidebar's one live `useQuery`. With
 * nothing held it shows nothing, never a fabricated `0`. `useQuery` would be wrong here even though
 * it skips a FRESH entry: when the entry is missing or failed (a 503, an outage), it fetches again
 * on every mount, which is every page change.
 */
export function useStoredQuery<T>(descriptor: QueryDescriptor<T>): { data: T | null; fetchedAt: number | null } {
  const entry = useSyncExternalStore(dataStore.subscribe, () => dataStore.entry<T>(descriptor.key));
  return { data: (entry?.value as T | undefined) ?? null, fetchedAt: entry?.fetchedAt ?? null };
}

/** Live prefetch progress plus store size, for the Settings readout. */
export function useStoreStatus(): { progress: PrefetchProgress; stats: StoreStats } {
  const { prefetcher } = useStoreActions();

  const progress = useSyncExternalStore(prefetcher.subscribe, prefetcher.getProgress);

  // Stats read `localStorage` directly, so they are sampled on store changes rather than derived
  // from a snapshot — `useSyncExternalStore` would need a stable object identity this cannot give.
  const [stats, setStats] = useState<StoreStats>(() => dataStore.stats());

  useEffect(() => {
    const sample = () => setStats(dataStore.stats());
    sample();
    return dataStore.subscribe(sample);
  }, []);

  return { progress, stats };
}
